import { t } from "@travily/i18n";
import { ResponsiveText } from "@ui/components/ResponsiveText";
import { SkipLink } from "@ui/components/SkipLink";
import { TopBarWeb } from "@ui/components/TopBar/TopBarWeb";
import { Button as RACButton } from "@ui/primitives/Button";
import { Link } from "@ui/primitives/Link";
import type { ReactNode } from "react";

export interface AuthFrameLink {
  /** Stable identity for React keys (e.g. "about", "logout"). */
  id: string;
  label: ReactNode;
  /** Renders as an anchor when set. */
  href?: string;
  /** Renders as a button action when set instead (e.g. logout posts via the router). */
  onPress?: () => void;
}

export function aboutNavLink(): AuthFrameLink {
  return {
    id: "about",
    href: "https://livetravily.com",
    label: <ResponsiveText base={t("common.navigation.about")} lg={t("common.navigation.about_travily")} />,
  };
}

// Mirrors the legacy link-style top-bar buttons: neutral-700 semibold text that
// darkens and underlines on hover, md type below lg and lg type above.
const navLinkClasses =
  "cursor-pointer text-base font-semibold text-neutral-700 underline-offset-2 outline-none hover:text-neutral-900 hover:underline focus-visible:underline lg:text-lg";

function AuthFrameLinks({ links }: { links: AuthFrameLink[] }) {
  return (
    <div className="flex items-center gap-6 md:gap-8">
      {links.map((link) =>
        link.href !== undefined ? (
          <Link key={link.id} className={navLinkClasses} href={link.href}>
            {link.label}
          </Link>
        ) : (
          <RACButton key={link.id} className={navLinkClasses} onPress={link.onPress}>
            {link.label}
          </RACButton>
        ),
      )}
    </div>
  );
}

export interface AuthFrameProps {
  children: ReactNode;
  navLinks?: AuthFrameLink[];
}

/**
 * Pre-session page chrome (login, waitlist): top bar with optional nav links, no app nav.
 * Deliberately not built on `PageFrame` — these pages render before a session exists, so
 * there are no Inertia shared props for `usePage` to read.
 */
export function AuthFrame({ children, navLinks = [] }: AuthFrameProps) {
  return (
    <div className="flex min-h-screen flex-col overflow-hidden bg-white">
      <SkipLink targetId="main-content" />
      <TopBarWeb constrained endContent={navLinks.length > 0 ? <AuthFrameLinks links={navLinks} /> : undefined} />

      <main id="main-content" className="flex flex-1 justify-center px-4 pb-4 xl:px-8">
        {children}
      </main>
    </div>
  );
}
